import {useEffect} from 'react';
import {ValueValidator} from '../Validator';
import {RootState} from "../../../../app/store";
import {useAppSelector} from "../../../../app/hooks";
import {OnValidChangeHandler} from "../component/ValidationComponent";


interface Props<PROPERTY, OBJECT> {
    selector: (state: RootState) => PROPERTY | undefined;
    anchorValueSelector: (state: RootState) => any | undefined;
    required?: boolean;
    fieldName: keyof OBJECT;
    onChange: (value: PROPERTY) => any,
    updateValidationStatus: OnValidChangeHandler<OBJECT>,
    validator?: ValueValidator<PROPERTY>;
    disabled?: boolean;
    label: string;
    child: (
        label: string,
        value: PROPERTY | undefined,
        onChange: (value: PROPERTY) => any,
        required: boolean
    ) => JSX.Element | JSX.Element[];
}

export const DependantValidationComponent = <PROPERTY, OBJECT>({
                                                                   selector,
                                                                   anchorValueSelector,
                                                                   required = true,
                                                                   fieldName,
                                                                   label,
                                                                   onChange,
                                                                   updateValidationStatus,
                                                                   validator,
                                                                   disabled,
                                                                   child
                                                               }: Props<PROPERTY, OBJECT>) => {

    const value = useAppSelector(selector);
    const anchorValue = useAppSelector(anchorValueSelector);

    useEffect(() => {
        let cancelled = false;

        if (validator && !disabled) {
            const maybePromise = validator(label, value);

            if (maybePromise instanceof Promise) {
                maybePromise
                    .then(outcome => {
                        if (!cancelled) {
                            onValidationStatusChange({...outcome, dirty: !!value});
                        }
                    })
                    .catch(() => {
                        if (!cancelled) {
                            onValidationStatusChange({valid: false, dirty: !!value, message: "Validation failed"});
                        }
                    });
            } else {
                onValidationStatusChange({...maybePromise, dirty: !!value});
            }
        } else {
            onValidationStatusChange({dirty: false, valid: true});
        }

        return () => {
            cancelled = true; // prevents late async updates
            onValidationStatusChange({valid: true, dirty: false});
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, disabled, anchorValue]);


    function onValidationStatusChange(validationOutcome: { valid: false; message?: string; dirty: boolean } | {
        valid: true,
        dirty: boolean
    }) {
        if (!validationOutcome.valid) {
            const {valid, message} = validationOutcome;
            updateValidationStatus({valid, fieldName, dirty: validationOutcome.dirty, message});
        } else {
            updateValidationStatus({valid: true, fieldName, dirty: validationOutcome.dirty});
        }
    }

    const handleChange = (value: PROPERTY) => {
        onChange(value);
    }

    return <>
        {child(label, value, handleChange, required)}
    </>;
};

